import { useSearchParams, useLocation } from "react-router-dom";
import { useMemo, useEffect } from "react";

interface UseGetPaginationOptions {
    defaultPage?: string;
    defaultPageSize?: string;
    defaultSortBy?: string;
    defaultOrderBy?: string;
    defaultSearchText?: string;
}

interface PaginationState {
    page: string;
    pageSize: string;
    sortBy: string;
    orderBy: string;
    searchText: string;
    setPage: (page: string) => void;
    setPageSize: (pageSize: string) => void;
    setSortBy: (sortBy: string) => void;
    setOrderBy: (orderBy: string) => void;
    setSearchText: (searchText: string) => void;
}

export default function usePagination(
    options: UseGetPaginationOptions = {}
): PaginationState {
    const {
        defaultPage = "1",
        defaultPageSize = "10",
        defaultSortBy = "",
        defaultOrderBy = "ASC",
        defaultSearchText = "",
    } = options;

    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    // Initialize URL params when they are missing
    useEffect(() => {
        const currentPage = searchParams.get("page");
        const currentPageSize = searchParams.get("pageSize");
        const currentSortBy = searchParams.get("sortBy");
        const currentOrderBy = searchParams.get("orderBy");

        const needsUpdate =
            !currentPage ||
            !currentPageSize ||
            !currentOrderBy ||
            (defaultSortBy && !currentSortBy);

        if (needsUpdate) {
            setSearchParams(
                (params) => {
                    if (!currentPage) {
                        params.set("page", defaultPage);
                    }
                    if (!currentPageSize) {
                        params.set("pageSize", defaultPageSize);
                    }
                    if (!currentOrderBy) {
                        params.set("orderBy", defaultOrderBy);
                    }
                    if (defaultSortBy && !currentSortBy) {
                        params.set("sortBy", defaultSortBy);
                    }
                    if (defaultSearchText && !searchParams.get("searchText")) {
                        params.set("searchText", defaultSearchText);
                    }
                    return params;
                },
                { replace: true }
            );
        }
    }, [
        location.search, // This will trigger when query params change or are removed
        defaultPage,
        defaultPageSize,
        defaultSortBy,
        defaultOrderBy,
        defaultSearchText,
        searchParams,
        setSearchParams,
    ]);

    // Derive values directly from URL params with defaults
    const paginationState = useMemo(() => {
        return {
            page: searchParams.get("page") || defaultPage,
            pageSize: searchParams.get("pageSize") || defaultPageSize,
            sortBy: searchParams.get("sortBy") || defaultSortBy,
            orderBy: searchParams.get("orderBy") || defaultOrderBy,
            searchText: searchParams.get("searchText") || defaultSearchText,
        };
    }, [
        searchParams,
        defaultPage,
        defaultPageSize,
        defaultSortBy,
        defaultOrderBy,
        defaultSearchText,
    ]);

    const setPage = (page: string) => {
        setSearchParams(
            (params) => {
                params.set("page", page);
                return params;
            },
            { replace: true }
        );
    };

    const setPageSize = (pageSize: string) => {
        setSearchParams(
            (params) => {
                params.set("pageSize", pageSize);
                params.set("page", "1");
                return params;
            },
            { replace: true }
        );
    };

    const setSortBy = (sortBy: string) => {
        setSearchParams(
            (params) => {
                params.set("sortBy", sortBy);
                return params;
            },
            { replace: true }
        );
    };

    const setOrderBy = (orderBy: string) => {
        setSearchParams(
            (params) => {
                params.set("orderBy", orderBy);
                return params;
            },
            { replace: true }
        );
    };

    const setSearchText = (searchText: string) => {
        setSearchParams(
            (params) => {
                if (searchText) {
                    params.set("searchText", searchText);
                } else {
                    params.delete("searchText");
                }
                params.set("page", "1");
                return params;
            },
            { replace: true }
        );
    };

    return {
        ...paginationState,
        setPage,
        setPageSize,
        setSortBy,
        setOrderBy,
        setSearchText,
    };
}